import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { getFormById } from '@/actions';
import WithImages from './variants/WithImages';
import ImageLeftFormRight from './variants/ImageLeftFormRight';
import ContentFormSplit from './variants/ContentFormSplit';

/**
 * Props for `LeadCaptureHero`.
 */
export type LeadCaptureHeroProps =
  SliceComponentProps<Content.LeadCaptureHeroSlice>;

/**
 * Server dispatcher for "LeadCaptureHero" Slices.
 *
 * `with_images` stays a self-contained client component. `imageLeftFormRight`
 * and `contentFormSplit` are hybrid variations: the HubSpot field schema is
 * fetched server-side here (so `FormLayout` can render it without a client
 * round-trip) and passed to the client variant.
 */
const LeadCaptureHero = async ({ slice }: LeadCaptureHeroProps) => {
  if (slice.variation === 'imageLeftFormRight') {
    const { form_id } =
      slice.primary as Content.LeadCaptureHeroSliceImageLeftFormRightPrimary;
    // Only fetch when a form id is configured. Fetching with an empty id would
    // return getFormById's generic fallback form, which reads as a real (but
    // wrong) "unknown" form to editors. Pass null so the variant shows a clear
    // "not configured" placeholder instead.
    const formData = form_id ? await getFormById(form_id) : null;
    return <ImageLeftFormRight slice={slice} formData={formData} />;
  }

  if (slice.variation === 'contentFormSplit') {
    const { form_id } =
      slice.primary as Content.LeadCaptureHeroSliceContentFormSplitPrimary;
    const formData = form_id ? await getFormById(form_id) : null;
    return <ContentFormSplit slice={slice} formData={formData} />;
  }

  return <WithImages slice={slice} />;
};

export default LeadCaptureHero;
